import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import {
  Container,
  Typography,
  Box,
  Paper,
  Alert,
  CircularProgress,
  Button,
  Chip,
} from '@mui/material';
import {
  CheckCircle as SuccessIcon,
  Error as ErrorIcon,
  Info as InfoIcon,
} from '@mui/icons-material';

interface CallbackResult {
  success: boolean;
  sessionId?: string;
  message: string;
  cardToken?: string;
  maskedCardNumber?: string;
  cardBrand?: string;
  error?: string;
}

const CardCallback: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [result, setResult] = useState<CallbackResult | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const processCallback = async () => {
      setLoading(true);
      
      try {
        // Extract parameters from URL
        const sessionId = searchParams.get('sessionId');
        const resultCode = searchParams.get('resultCode') || searchParams.get('result');
        const tokenId = searchParams.get('tokenId') || searchParams.get('token');
        const maskedNumber = searchParams.get('maskedNumber') || searchParams.get('maskedCardNumber');
        const cardBrand = searchParams.get('cardBrand') || searchParams.get('brand');
        const errorCode = searchParams.get('errorCode') || searchParams.get('error');
        
        console.log('eSCOTT callback received:', {
          sessionId,
          resultCode,
          tokenId,
          maskedNumber,
          cardBrand,
          errorCode
        });

        if (resultCode === '00' || resultCode === 'success') {
          // Success case
          setResult({
            success: true,
            sessionId: sessionId || undefined,
            message: 'カード登録が正常に完了しました',
            cardToken: tokenId || `card_token_${Date.now()}`,
            maskedCardNumber: maskedNumber || '****-****-****-1111',
            cardBrand: cardBrand || 'VISA'
          });
        } else if (errorCode || resultCode === 'error' || resultCode === 'cancel') {
          // Error case
          setResult({
            success: false,
            sessionId: sessionId || undefined,
            message: 'カード登録が失敗またはキャンセルされました',
            error: errorCode || 'User cancelled or unknown error'
          });
        } else {
          // Unknown case - treat as success for demo
          setResult({
            success: true,
            sessionId: sessionId || undefined,
            message: 'カード登録が完了しました（パラメータ不明）',
            cardToken: `demo_token_${Date.now()}`,
            maskedCardNumber: '****-****-****-1111',
            cardBrand: 'UNKNOWN'
          });
        }
      } catch (error) {
        console.error('Error processing callback:', error);
        setResult({
          success: false,
          message: 'コールバック処理中にエラーが発生しました',
          error: error instanceof Error ? error.message : 'Unknown error'
        });
      } finally {
        setLoading(false);
      }
    };

    processCallback();
  }, [searchParams]);

  const handleReturnToDebug = () => {
    navigate('/debug');
  };

  const handleReturnToDashboard = () => {
    navigate('/dashboard');
  };

  if (loading) {
    return (
      <Container maxWidth="md" sx={{ mt: 4 }}>
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <CircularProgress size={60} sx={{ mb: 2 }} />
          <Typography variant="h6">
            eSCOTTからのコールバックを処理中...
          </Typography>
          <Typography variant="body2" color="text.secondary">
            カード登録結果を確認しています
          </Typography>
        </Paper>
      </Container>
    );
  }

  return (
    <Container maxWidth="md" sx={{ mt: 4 }}>
      <Paper sx={{ p: 4 }}>
        <Box display="flex" alignItems="center" gap={2} mb={3}>
          {result?.success ? (
            <SuccessIcon color="success" sx={{ fontSize: 48 }} />
          ) : (
            <ErrorIcon color="error" sx={{ fontSize: 48 }} />
          )}
          <Box>
            <Typography variant="h4">
              カード登録結果
            </Typography>
            <Typography variant="body1" color="text.secondary">
              eSCOTT決済サービスからのコールバック
            </Typography>
          </Box>
        </Box>

        <Alert 
          severity={result?.success ? 'success' : 'error'} 
          sx={{ mb: 3 }}
          icon={result?.success ? <SuccessIcon /> : <ErrorIcon />}
        >
          {result?.message}
        </Alert>

        {result?.success && (
          <Box sx={{ mb: 3 }}>
            <Typography variant="h6" gutterBottom>
              登録されたカード情報
            </Typography>
            
            <Box display="flex" flexWrap="wrap" gap={1} mb={2}>
              {result.cardToken && (
                <Chip 
                  label={`トークン: ${result.cardToken}`}
                  variant="outlined"
                  icon={<InfoIcon />}
                />
              )}
              {result.maskedCardNumber && (
                <Chip 
                  label={`カード番号: ${result.maskedCardNumber}`}
                  variant="outlined"
                  color="primary"
                />
              )}
              {result.cardBrand && (
                <Chip 
                  label={`ブランド: ${result.cardBrand}`}
                  variant="outlined"
                  color="secondary"
                />
              )}
            </Box>
          </Box>
        )}

        {result?.error && (
          <Box sx={{ mb: 3 }}>
            <Typography variant="h6" gutterBottom color="error">
              エラー詳細
            </Typography>
            <Alert severity="error">
              {result.error}
            </Alert>
          </Box>
        )}

        {result?.sessionId && (
          <Box sx={{ mb: 3 }}>
            <Typography variant="body2" color="text.secondary">
              <strong>セッションID:</strong> {result.sessionId}
            </Typography>
          </Box>
        )}

        <Box display="flex" gap={2} justifyContent="center">
          <Button
            variant="outlined"
            onClick={handleReturnToDebug}
            size="large"
          >
            デバッグ画面に戻る
          </Button>
          <Button
            variant="contained"
            onClick={handleReturnToDashboard}
            size="large"
          >
            ダッシュボードに戻る
          </Button>
        </Box>

        <Box sx={{ mt: 4, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
          <Typography variant="body2" color="text.secondary">
            <strong>開発者向け情報:</strong><br />
            このページは、eSCOTTカード登録画面からのリダイレクト先として機能します。
            実際の本番環境では、このコールバック処理によって決済フローが継続されます。
          </Typography>
        </Box>
      </Paper>
    </Container>
  );
};

export default CardCallback;